import React, { useEffect, useRef, useState } from "react";

import Modal from "react-modal";
import Select from "react-select";

import * as api from "../../api";
import * as notify from "../../notify";
import useDebounce from "../../useDebounce";

const POLICY_PLC_ALL = "POLICY_PLC_ALL";
const PLC_SEARCH_DEBOUNCE_MS = 900;

const getProfileValue = (profile, ...keys) => {
  for (const key of keys) {
    if (profile && profile[key] !== undefined && profile[key] !== null) {
      return profile[key];
    }
  }

  return "";
};

const getPlcOption = (plc) => {
  if (typeof plc === "string") {
    return { value: plc, label: plc };
  }

  const id = plc.id || plc.plcId || "";
  const name = plc.displayName || plc.name || "";
  return {
    value: id,
    label: name ? `${id} - ${name}` : id,
  };
};

const getLocationOption = (location) => {
  if (typeof location === "string" || typeof location === "number") {
    return { value: String(location), label: `${location}` };
  }

  const id = location.id != null ? String(location.id) : "";
  const name = location.name || "";
  return {
    value: id,
    label: name ? `${name} (${id})` : `${id}`,
  };
};

const mergeOptions = (...optionLists) => {
  const merged = [];
  const seen = new Set();

  optionLists.flat().forEach((option) => {
    if (!option || option.value === "" || option.value == null) {
      return;
    }
    const key = String(option.value);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    merged.push({ ...option, value: key });
  });

  return merged;
};

const VtUserManagementEditUser = (props) => {
  const { onRequestClose, onUpdated, showModal, user } = props;
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [profile, setProfile] = useState(null);
  const [profilePolicies, setProfilePolicies] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [selectedPlcs, setSelectedPlcs] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [plcOptions, setPlcOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [plcSearchInput, setPlcSearchInput] = useState("");
  const [searchingPlcs, setSearchingPlcs] = useState(false);
  const [plcSearchCompleted, setPlcSearchCompleted] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const assignedLocationsRef = useRef([]);
  const debouncedPlcSearchInput = useDebounce(
    plcSearchInput,
    PLC_SEARCH_DEBOUNCE_MS
  );

  const profileId = user.subId || user.id;
  const hasPlcAllPolicy = profilePolicies.includes(POLICY_PLC_ALL);
  // Edit always surfaces the target user's locations for both admin types.
  const showLocations =
    selectedLocations.length > 0 || locationOptions.length > 0;
  const isValid =
    firstName.trim() && lastName.trim() && selectedPlcs.length > 0;

  const resetForm = () => {
    setProfile(null);
    setProfilePolicies([]);
    setFirstName("");
    setLastName("");
    setSelectedPlcs([]);
    setSelectedLocations([]);
    assignedLocationsRef.current = [];
    setPlcOptions([]);
    setLocationOptions([]);
    setPlcSearchInput("");
    setSearchingPlcs(false);
    setPlcSearchCompleted(false);
    setLoadingLocations(false);
  };

  const hydrateForm = (targetProfile, actingProfile, rolesResponse) => {
    const targetPlcs = Array.isArray(targetProfile.plcs)
      ? targetProfile.plcs.map(getPlcOption)
      : [];
    const targetLocations = Array.isArray(targetProfile.locations)
      ? targetProfile.locations.map(getLocationOption)
      : [];
    const assignablePlcs =
      actingProfile && Array.isArray(actingProfile.plcs)
        ? actingProfile.plcs.map(getPlcOption)
        : [];
    const assignableLocations =
      actingProfile && Array.isArray(actingProfile.locations)
        ? actingProfile.locations.map(getLocationOption)
        : [];
    const roles = Array.isArray(rolesResponse) ? rolesResponse : [];
    const profileRole =
      actingProfile && actingProfile.role ? actingProfile.role : "";
    const currentRole = roles.find((role) => role.id === profileRole);
    const policies =
      currentRole && currentRole.policies
        ? currentRole.policies
        : actingProfile && actingProfile.policies
        ? actingProfile.policies
        : [];

    setProfile(targetProfile);
    setFirstName(getProfileValue(targetProfile, "firstName", "first_name"));
    setLastName(getProfileValue(targetProfile, "lastName", "last_name"));
    setSelectedPlcs(targetPlcs);
    setSelectedLocations(targetLocations);
    assignedLocationsRef.current = targetLocations;
    setProfilePolicies(policies);
    setPlcOptions(
      policies.includes(POLICY_PLC_ALL)
        ? targetPlcs
        : mergeOptions(targetPlcs, assignablePlcs)
    );
    setLocationOptions(
      policies.includes(POLICY_PLC_ALL)
        ? targetLocations
        : mergeOptions(targetLocations, assignableLocations)
    );
  };

  const handleCloseModal = () => {
    if (onRequestClose) {
      onRequestClose();
    }
  };

  useEffect(() => {
    if (!showModal) {
      return;
    }

    if (!profileId) {
      notify.error("Unable to load user profile");
      handleCloseModal();
      return;
    }

    resetForm();
    setLoadingProfile(true);

    Promise.all([
      api.getVtUserManagementUserProfile(profileId),
      api.getVtUserManagementProfile(),
      api.getVtUserManagementRoles(),
    ]).then(([targetProfile, actingProfile, rolesResponse]) => {
      if (targetProfile && targetProfile.error) {
        notify.error("Failed to load user profile");
      } else {
        hydrateForm(targetProfile, actingProfile, rolesResponse);
      }
      if (actingProfile && actingProfile.error) {
        notify.error("Failed to load assignable PLCs and locations");
      }
      if (rolesResponse && rolesResponse.error) {
        notify.error("Failed to load current user roles");
      }
      setLoadingProfile(false);
    });
  }, [showModal, profileId]);

  const handlePlcChange = (value) => {
    setSelectedPlcs(value || []);
  };

  const handlePlcSearchInputChange = (value, actionMeta) => {
    if (!hasPlcAllPolicy || actionMeta.action !== "input-change") {
      return value;
    }

    setPlcSearchInput(value);
    setPlcSearchCompleted(false);
    return value;
  };

  useEffect(() => {
    if (!hasPlcAllPolicy) {
      return;
    }

    const trimmed = debouncedPlcSearchInput.trim();
    if (!trimmed || trimmed.length < 2) {
      setSearchingPlcs(false);
      setPlcSearchCompleted(false);
      return;
    }

    let cancelled = false;
    setSearchingPlcs(true);
    setPlcSearchCompleted(false);
    api.searchVtUserManagementPlcs(trimmed).then((response) => {
      if (cancelled) {
        return;
      }

      const searchResults =
        response && response.searchResult ? response.searchResult : [];
      const searchOptions = searchResults.map((plc) => ({
        value: plc.id,
        label: `${plc.id} - ${plc.name} - ${plc.account_type} - (${plc.state})`,
      }));
      setPlcOptions((currentOptions) =>
        mergeOptions(searchOptions, currentOptions)
      );
      if (response && response.error) {
        notify.error("Failed to search PLCs");
      }
      setSearchingPlcs(false);
      setPlcSearchCompleted(true);
    });

    return () => {
      cancelled = true;
    };
  }, [debouncedPlcSearchInput, hasPlcAllPolicy]);

  useEffect(() => {
    if (!hasPlcAllPolicy) {
      return;
    }

    if (!selectedPlcs.length) {
      // Keep the user's already-assigned locations visible even with no PLCs selected.
      setLocationOptions(assignedLocationsRef.current);
      setSelectedLocations(assignedLocationsRef.current);
      return;
    }

    let cancelled = false;
    setLoadingLocations(true);
    const plcIds = selectedPlcs.map((option) => option.value);
    api.getVtUserManagementLocationsByPlcs(plcIds).then((response) => {
      if (cancelled) {
        return;
      }

      const locations =
        response && response.locations ? response.locations : [];
      const options = locations.map(getLocationOption);

      // Keep the target user's assigned locations selected and in the options list.
      // PLC fetch only adds assignable options; it must not drop existing assignments.
      setSelectedLocations((current) => {
        const preserved =
          current.length > 0 ? current : assignedLocationsRef.current;
        setLocationOptions(
          mergeOptions(options, preserved, assignedLocationsRef.current)
        );
        return preserved;
      });

      if (response && response.error) {
        notify.error("Failed to load locations for selected PLCs");
      }
      setLoadingLocations(false);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedPlcs, hasPlcAllPolicy]);

  const getPlcNoOptionsMessage = () => {
    if (!hasPlcAllPolicy) {
      return "No PLCs available";
    }

    if (searchingPlcs) {
      return "Searching PLCs...";
    }

    if (debouncedPlcSearchInput.trim().length >= 2 && plcSearchCompleted) {
      return "No PLCs found";
    }

    return "Type at least 2 characters to search PLCs";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid || submitting) {
      return;
    }

    setSubmitting(true);
    const payload = {
      type: getProfileValue(profile, "type") || "CU",
      id: getProfileValue(profile, "id", "pk_id"),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: getProfileValue(profile, "email"),
      userStatus: getProfileValue(profile, "userStatus", "status"),
      priority: getProfileValue(profile, "priority"),
      role: getProfileValue(profile, "role") || "ROLE_VIRTUAL_TERMINAL",
      policies: getProfileValue(profile, "role") || "ROLE_VIRTUAL_TERMINAL",
      sub_id: getProfileValue(profile, "subId", "sub_id") || profileId,
      plcs: selectedPlcs.map((option) => option.value),
      locations: showLocations
        ? selectedLocations
            .map((option) => parseInt(option.value, 10))
            .filter((locationId) => !Number.isNaN(locationId))
        : [],
      tempPassword: getProfileValue(profile, "tempPassword"),
      displayLocationsTable: showLocations,
      displayPlcTable: true,
    };

    api
      .updateVtUserManagementUserProfile(profileId, payload)
      .then((response) => {
        setSubmitting(false);
        const data = response && response.data;
        if (data && data.status === "SUCCESS") {
          notify.success("User updated", true);
          handleCloseModal();
          if (onUpdated) {
            onUpdated();
          }
        } else {
          notify.error((data && data.error) || "Failed to update user");
        }
      });
  };

  const email = getProfileValue(profile, "email");
  const accountStatus = getProfileValue(profile, "status");
  const cognitoUserStatus = getProfileValue(profile, "userStatus");

  return (
    <>
      <Modal
        isOpen={!!showModal}
        shouldCloseOnOverlayClick={true}
        onRequestClose={handleCloseModal}
        className="Modal add-user edit-user"
        overlayClassName="Overlay deny"
        ariaHideApp={false}
      >
        <h3>Edit User</h3>
        {loadingProfile && <div className="edit-user-loading">Loading...</div>}
        {!loadingProfile && profile && (
          <form name="edit-user" onSubmit={handleSubmit}>
            <div className="edit-user-header">
              <div className="edit-user-summary">
                <div>
                  <span>Email</span>
                  <strong>{email || "-"}</strong>
                </div>
                <div>
                  <span>Account Status</span>
                  <strong>{accountStatus || "-"}</strong>
                </div>
                <div>
                  <span>Cognito Status</span>
                  <strong>{cognitoUserStatus || "-"}</strong>
                </div>
              </div>
            </div>
            <div className="edit-user-name-row">
              <div className="add-user-form">
                <label htmlFor="edit-user-first-name">First Name</label>
                <input
                  id="edit-user-first-name"
                  className="proview-input long"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="add-user-form">
                <label htmlFor="edit-user-last-name">Last Name</label>
                <input
                  id="edit-user-last-name"
                  className="proview-input long"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>
            <div className="add-user-form">
              <label htmlFor="edit-user-plcs">
                {hasPlcAllPolicy ? "Search PLCs" : "PLCs"}
              </label>
              <Select
                inputId="edit-user-plcs"
                isMulti
                isLoading={searchingPlcs}
                options={plcOptions}
                value={selectedPlcs}
                onChange={handlePlcChange}
                onInputChange={handlePlcSearchInputChange}
                openMenuOnClick={!hasPlcAllPolicy}
                noOptionsMessage={getPlcNoOptionsMessage}
                classNamePrefix="select"
                placeholder={hasPlcAllPolicy ? "Search PLCs" : "Select PLCs"}
              />
            </div>
            {showLocations && (
              <div className="add-user-form">
                <label htmlFor="edit-user-locations">Locations</label>
                <Select
                  inputId="edit-user-locations"
                  isMulti
                  isLoading={loadingLocations}
                  options={locationOptions}
                  value={selectedLocations}
                  onChange={(value) => setSelectedLocations(value || [])}
                  classNamePrefix="select"
                  placeholder="Select Locations"
                />
              </div>
            )}
            <div className="add-user-actions edit-user-footer-actions">
              <button
                className="proview-button cancel left"
                onClick={handleCloseModal}
                type="button"
              >
                Cancel
              </button>
              <button
                className="proview-button right"
                type="submit"
                disabled={!isValid || submitting}
              >
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        )}
        {!loadingProfile && !profile && <div>User profile unavailable.</div>}
      </Modal>
    </>
  );
};

export default VtUserManagementEditUser;
